import { useState, useEffect, useCallback } from "react";

/**
 * Hook to get the height of an element.
 * Uses a callback ref backed by state so the observer re-attaches correctly
 * whenever the element mounts, re-mounts, or changes (e.g. after a conditional
 * early-return in the parent component resolves).
 *
 * @param observePadding - Whether to subtract padding from the reported height
 * @returns [callbackRef, contentHeight, height]
 */
export const useElementHeight = <T extends HTMLElement>(
  observePadding = false
): [(node: T | null) => void, number, number] => {
  const [element, setElement] = useState<T | null>(null);
  const [height, setHeight] = useState<number>(0);
  const [contentHeight, setContentHeight] = useState<number>(0);

  const callbackRef = useCallback((node: T | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return;

    const updateSize = () => {
      const computedStyle = getComputedStyle(element);

      const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0;
      const elementHeight = element.offsetHeight;

      const nextContentHeight = observePadding ? elementHeight - paddingTop - paddingBottom : elementHeight;

      setHeight(previousHeight => (Math.abs(previousHeight - elementHeight) < 2 ? previousHeight : elementHeight));
      setContentHeight(previousContentHeight =>
        Math.abs(previousContentHeight - nextContentHeight) < 2 ? previousContentHeight : nextContentHeight
      );
    };

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(element);

    updateSize();

    return () => {
      observer.disconnect();
    };
  }, [element, observePadding]);

  return [callbackRef, contentHeight, height];
};
